import React, { useState } from 'react';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { staffAttApi, errMsg } from '@/services/staffAttendanceApi';

const istLocal = (iso) => new Date(iso).toLocaleString('sv-SE', { timeZone: 'Asia/Kolkata' }).slice(0, 16).replace(' ', 'T');
const fmt = (iso) => (iso ? new Date(iso).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—');
const ACTION = { auto_closed: 'Auto-closed at day close', fixed: 'Check-out entered', edited: 'Check-out changed' };

/** HR: enter the real check-out time (+ reason) for a session left open at day close. */
export const FixCheckoutDialog = ({ row, onClose, onSaved }) => {
  const [outAt, setOutAt] = useState(row.fixed_out_time ? istLocal(row.fixed_out_time) : `${row.date}T18:00`);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await staffAttApi.fixMissed(row.missed_id, { out_at: outAt, reason });
      toast.success(`Check-out saved for ${row.staff_name}`);
      onSaved();
    } catch (e) { toast.error(errMsg(e, 'Could not save the check-out')); } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="missed-fix-dialog">
        <DialogHeader>
          <DialogTitle>{row.staff_name} — {row.date}</DialogTitle>
          <DialogDescription>Checked in {fmt(row.in_time)} (session {row.session_no}). Enter the real check-out; it is saved as approved and the hours are added.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div><label className="text-xs text-gray-500">Check-out date &amp; time (IST) — after midnight for OT</label>
            <Input type="datetime-local" value={outAt} onChange={(e) => setOutAt(e.target.value)} className="bg-white" data-testid="missed-fix-out-input" /></div>
          <div><label className="text-xs text-gray-500">Reason (required)</label>
            <Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Forgot to check out — confirmed with supervisor" rows={2} data-testid="missed-fix-reason-input" /></div>
          <div className="rounded-lg bg-gray-50 border p-2 space-y-1 max-h-40 overflow-y-auto" data-testid="missed-fix-history">
            {(row.history || []).map((h, i) => (
              <div key={`${h.at}-${i}`} className="text-[11px] text-gray-600" data-testid={`missed-history-${i}`}>
                <b>{ACTION[h.action] || h.action}</b>{h.out_time ? ` → ${fmt(h.out_time)}` : ''} · {h.by_name} · {fmt(h.at)}{h.reason ? ` — ${h.reason}` : ''}
              </div>
            ))}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="missed-fix-cancel">Cancel</Button>
          <Button onClick={save} disabled={busy || reason.trim().length < 3 || !outAt} className="bg-amber-600 hover:bg-amber-700" data-testid="missed-fix-save">
            {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Save check-out
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default FixCheckoutDialog;
